import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { formatDate } from "@angular/common";
import { ChangeDetectorRef, Component, input, output, ChangeDetectionStrategy } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import { AppService } from "../../../app.service";
import { HeaderFooterRow, HeaderFooterRowComponent } from "../../../models/headerFooterRow";
import { MetadataInfo } from "../../../models/metadataInfo";
import { MoovVariable } from "../../../models/moovVariable";
import { moveItemInFormArray } from "../../../theme/utils/app-helpers";

@Component({ standalone:false,
    selector: 'app-edit-row',
    templateUrl: './edit-row.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./edit-row.component.scss']
  })
  export class EditRowComponent {
    readonly row = input.required<HeaderFooterRow>();
    readonly availableMetadataInfo = input<MetadataInfo[] | undefined>(undefined);
    readonly availableCustomerVariables = input<MoovVariable[] | undefined>(undefined);
    readonly fileType = input<string | undefined>(undefined);
    readonly fieldDelimiter = input<string | undefined>(undefined);

    readonly onValueChanged = output<{
        rowIndex: number;
        type: string;
        fieldDelimiter: string;
        formValid: boolean;
        formValue: string;
        formDirty: boolean;
    }>();

    public rowForm:FormGroup;

    private rowFormComponentsValueChangesSubscription?:Subscription;

    private fgSubscriptionArray:Subscription[]=[];

    public formatTypeArray=[{name:'CSV', value:'csv'}, {name:'Fixed Length', value:"fl"}];
    public fieldDelimiterArray=[',', ';', '|', '*', '\t'];

    constructor(private cd:ChangeDetectorRef){
        this.rowForm = new FormGroup({
            rowIndex: new FormControl<number>(0, Validators.required),
            type: new FormControl<string | undefined>(undefined, Validators.required),
            fieldDelimiter: new FormControl<string | undefined>(undefined),
            components: new FormArray([]),
        });
    }

    ngOnInit() {
        this.populateForm();
        let sub1 = this.rowForm.controls.type.valueChanges.subscribe(_v=>{
            if(this.csvSelected){
                this.rowForm.controls['fieldDelimiter'].setValidators([Validators.required, Validators.min(1), Validators.max(1)]);
            }
            else{
                this.rowForm.controls['fieldDelimiter'].clearValidators();
            }
            const row = this.row();
            this.rowForm.controls.fieldDelimiter.setValue(row.fieldDelimiter?row.fieldDelimiter:this.fieldDelimiter());
            this.componentsChanged();
            this.cd.detectChanges();
        });
        let sub2 = this.rowForm.controls.fieldDelimiter.valueChanges.subscribe(_v=>{
            this.componentsChanged();
        });
        this.fgSubscriptionArray.push(sub1);
        this.fgSubscriptionArray.push(sub2);
        this.rowFormComponentsValueChangesSubscription = this.rowForm.controls['components'].valueChanges.subscribe(value=>this.componentsChanged());
    }

    ngOnDestroy(){
        if(this.rowFormComponentsValueChangesSubscription) this.rowFormComponentsValueChangesSubscription.unsubscribe();
        this.fgSubscriptionArray.forEach(s=>s.unsubscribe());
    }

    get rowComponentTypes(){
        return AppService.rowComponentTypeArray;
    }

    get rowDateTimePatterns(){
        return AppService.rowDateTimePatternArray;
    }

    get formArr() {
        return this.rowForm.get("components") as FormArray;
    }

    formGroup(index: number): FormGroup {
        return this.formArr.at(index) as FormGroup;
    }

    get formValueAsJson(){
        let componentArray:any[]=[];
        let index:number=0;
        const components = this.rowForm.get('components') as FormArray;
        components?.value.forEach((c:any)=>{
            let hfrc=new HeaderFooterRowComponent();
            hfrc.name=c.name;
            hfrc.index=index;
            hfrc.type=c.type?.id;
            hfrc.value=c.value;
            if(this.flSelected){
                hfrc.fieldLength=c.fieldLength;
                if(c.paddingDirection) hfrc.paddingDirection=c.paddingDirection;
                if(c.paddingCharacter) hfrc.paddingCharacter=c.paddingCharacter[0];
            }
            componentArray.push(hfrc);
            index++;
        });
        return JSON.stringify(componentArray);
    }

    get availablePaddingDirections(){
        return ["left", "right"];
    }

    get flSelected(){
        return this.rowForm.get('type')!.value?.value=="fl";
    }

    get csvSelected(){
        return this.rowForm.get('type')!.value?.value=="csv";
    }

    get canAddComponent(){
        return (this.csvSelected && this.rowForm.get('fieldDelimiter')!.value) || this.flSelected;
    }

    get genericFileType(){
        const fileType = this.fileType();
        if(fileType=='fl' || fileType =='mrt-fl' || fileType=="grt-fl"){
            return this.formatTypeArray.find(f=>f.value=='fl');
        }
        else if(fileType=='csv' || fileType =='mrt-csv' || fileType=="grt-csv"){
            return this.formatTypeArray.find(f=>f.value=='csv');
        }
        else return undefined;
    }

    private componentsChanged(){
        this.rowForm.updateValueAndValidity();
        this.emitRowData();
     }

    private emitRowData(){
        this.onValueChanged.emit({rowIndex:this.row().rowIndex, type:this.rowForm.get('type')!.value?.value, fieldDelimiter:this.rowForm.get('fieldDelimiter')!.value, formValid:this.rowForm.valid, formValue:this.formValueAsJson, formDirty:true});
    }

    private populateForm(){
        let formatType=undefined;
        const row = this.row();
        if(row.type) formatType = this.formatTypeArray.find(f=>f.value==this.row().type)
        else formatType=this.genericFileType
        this.rowForm.controls.type.setValue(formatType);
        this.rowForm.controls.fieldDelimiter.setValue(row.fieldDelimiter?row.fieldDelimiter:this.fieldDelimiter());
        if(row?.rowComponents){
            if(row.rowComponents){
                row.rowComponents.forEach(c=>{
                    this.addComponent(c.name,c.type,c.value,c.fieldLength,c.paddingDirection,c.paddingCharacter);
                });
            }
        }
        if(this.csvSelected){
            this.rowForm.controls['fieldDelimiter'].setValidators([Validators.required, Validators.min(1)]);
        }
        else{
            this.rowForm.controls['fieldDelimiter'].clearValidators();
        }
        this.rowForm.updateValueAndValidity();
        //this.emitRowData();
    }

    public updateParameterValue(value:string){
        this.deleteAllComponents();
        if(value){
            let parameterArray:HeaderFooterRowComponent[] = JSON.parse(value);
            if(parameterArray){
                parameterArray.forEach(p=>{
                    this.addComponent(p.name!,p.type!,p.value!,p.fieldLength,p.paddingDirection,p.paddingCharacter);
                });
            }
        }
    }

    public onAddComponentClick(){
        this.addComponent(undefined,undefined,undefined,undefined,undefined,undefined);
    }

    public drop(event: CdkDragDrop<any[]>) {
        moveItemInFormArray(this.formArr, event.previousIndex, event.currentIndex);
        this.emitRowData();
        this.rowForm.markAsDirty();
    }

    //#region components
    public createComponent(name:string|undefined, typeId:number|undefined, value:string|undefined, fieldLength:number|undefined, paddingDirection:string|undefined, paddingCharacter:string|undefined): FormGroup {
        let rowComponentType = (typeId && typeId>0)?this.rowComponentTypes.find(t=>t.id==typeId):null;
        let fbGroup =  new FormGroup({
            name: new FormControl<string|undefined>(name),
            type: new FormControl<any>(rowComponentType, Validators.required),
            value:new FormControl<string|undefined>(value),
            fieldLength: new FormControl<number|undefined>(fieldLength),
            paddingDirection:new FormControl<string|undefined>(paddingDirection),
            paddingCharacter:new FormControl<string|undefined>(paddingCharacter)
        });
        if(this.flSelected){
            fbGroup.controls['fieldLength'].setValidators([Validators.required, Validators.min(1)]);
        }
        else{
            fbGroup.controls['fieldLength'].clearValidators();
        }
        fbGroup.controls['type'].valueChanges.subscribe(d=>{
            fbGroup.controls['value'].setValue(null);
            if(d?.id!=2 && d?.id!=3) fbGroup.controls['value'].setValidators(Validators.required);
            else fbGroup.controls['value'].clearValidators();
            fbGroup.controls['value'].updateValueAndValidity();
        });
        return fbGroup;
    }

    public addComponent(name:string|undefined, typeId:number|undefined, value:string|undefined, fieldLength:number|undefined, paddingDirection:string|undefined, paddingCharacter:string|undefined) {
        if(this.rowForm){
            const fa = this.rowForm.get('components') as FormArray;
            let component=this.createComponent(name, typeId, value, fieldLength, paddingDirection, paddingCharacter);
            fa.push(component);
        }
    }

    public deleteComponent(componentIndex:number) {
        const components = this.rowForm.get('components') as FormArray;
        components.removeAt(componentIndex);
        components.markAsTouched();
    }

    public deleteAllComponents() {
        const components = this.rowForm.get('components') as FormArray;
        components.clear();
    }
    //#endregion

    get rowPreview(){
        let previewRow:string='';
        const components = this.rowForm.get('components') as FormArray;
        components.value.forEach((c:any)=>{
            let previewComponent:string='';
            switch(c.type?.id){
                case 1:
                    if(c.value) previewComponent = formatDate(Date.now(),c.value,'en-US');
                    break;
                case 2:
                    previewComponent= 'bec331aa15661f591bf10a709be9a710';
                    break;
                case 3:
                    if(c.value) previewComponent = c.value;
                    break;
                case 4:
                    if(c.value) previewComponent = '['+c.value+']';
                    break;
                case 5:
                    if(c.value) previewComponent = '['+c.value+']';
                    break;
                case 6:
                    if(c.value) previewComponent = '['+c.value+']';
                    break;
                default:
                    break;
            }
            if(this.csvSelected){
                previewRow += previewComponent + this.rowForm.controls['fieldDelimiter'].value;
            }
            else{
                //add padding rules here
                if(c.paddingDirection=="left"){
                    if(c.paddingCharacter){
                        previewComponent=previewComponent.padStart(c.fieldLength, c.paddingCharacter);
                    }
                    else{
                        previewComponent=previewComponent.padStart(c.fieldLength);
                    }
                }
                else{
                    if(c.paddingCharacter){
                        previewComponent=previewComponent.padEnd(c.fieldLength, c.paddingCharacter);
                    }
                    else{
                        previewComponent=previewComponent.padEnd(c.fieldLength);
                    }
                }
                previewRow += previewComponent;
            }
        });
        if(this.csvSelected){
            //remove trailing comma
            previewRow=previewRow.substring(0,previewRow.length-1);
        }
        return previewRow;
    }

  }  